import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { useLocation, useNavigate } from "react-router-dom";
import DashboardNav from '../../../components/Navbar/DashboardNav';
import { ExamService } from '../../../Services/examServices';
import LiveExam from '../../../Services/liveexam.services';
import { errorResponseHelper } from '../../../helpers/request.helper';
import AllPagesPDFViewer from './pdfview/AllPagesPdfViewer';
import useCountDown from 'react-countdown-hook';
import prettyMilliseconds from 'pretty-ms';
import "./Instructions.css";
import CustomModal from '../../../components/Modal/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle, faClock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

export interface CustomizedState {
    examId: string
    mappingId: string
}

export default function Instructions() {
    const [modalShow, setModalShow] = React.useState(false);
    const [timeLeft, { start, pause }] = useCountDown(0, 1000);
    const [instructionsCheck, setInstructionsCheck] = useState(false);
    const [disable, setDisable] = useState(true);
    const [instructions, setInstructions] = useState<string[]>();

    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as CustomizedState;

    const generateTokenForLiveExam = async () => {
        await LiveExam.generateTokenForLiveExam(state.examId, state.mappingId)
            .then((res) => {
                if (res.status === 200) {
                    sessionStorage.setItem("liveToken", res.data.data.token);
                    navigate('/live/exam-live', { state: { examId: state.examId, mappingId: state.mappingId } });
                }
            })
            .catch(e => errorResponseHelper(e));
    };

    const startExam = () => generateTokenForLiveExam();

    const getExamInstructions = async () => {
        await ExamService.getInstructions(state.examId)
            .then((res) => { if (res.status === 200) setInstructions(res.data.data.instructions); })
            .catch(e => console.error(e));
    };

    const startCountdown = async () => {
        await ExamService.getUserAllowedStartTime(state.examId)
            .then((res) => { if (res.status === 200 && res.data) start(res.data.data.startTime * 1000); });
    };

    useEffect(() => {
        startCountdown();
        getExamInstructions();
    }, []);

    useEffect(() => {
        if (timeLeft <= 0) { setDisable(false); pause(); }
        else setDisable(true);
    }, [timeLeft]);

   return (
    <>
        <DashboardNav />

        <div style={{
            minHeight: 'calc(100vh - 60px)',
            background: '#F4F6FB',
            display: 'flex',
            flexDirection: 'column'
        }}>

            {/* Header Section */}
            <div style={{
                padding: '1.5rem 2rem 1rem 2rem'
            }}>
                <h4 style={{
                    fontWeight: 700,
                    marginBottom: 4,
                    color: '#1A1A2E'
                }}>
                    Important Instructions
                </h4>
                <p style={{
                    margin: 0,
                    color: '#6C757D',
                    fontSize: '0.9rem'
                }}>
                    Please read carefully before starting your exam
                </p>
            </div>

            {/* FULL HEIGHT PDF SECTION */}
            <div style={{
                flex: 1,
                padding: '0 2rem',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #E6E9F5',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>

                    <div style={{
                        padding: '1rem 1.5rem',
                        borderBottom: '1px solid #EEF0F6',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: '#6B7280',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                    }}>
                        Exam Instructions
                    </div>

                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '1rem'
                    }}>
                        {instructions ? (
                            <AllPagesPDFViewer pdf={instructions} />
                        ) : (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <Spinner animation="border" />
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* STICKY FOOTER SECTION */}
            <div style={{
                background: '#ffffff',
                borderTop: '1px solid #E6E9F5',
                padding: '1rem 2rem',
                position: 'sticky',
                bottom: 0,
                zIndex: 10
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 20
                }}>

                    {/* Countdown */}
                    <div className="d-flex align-items-center gap-3">

                        {timeLeft > 0 ? (
                            <>
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    background: '#FEF3C7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <FontAwesomeIcon icon={faClock} style={{ color: '#F59E0B' }} />
                                </div>

                                <div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: '#6B7280',
                                        textTransform: 'uppercase'
                                    }}>
                                        Exam starts in
                                    </div>
                                    <div style={{
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        color: '#111827'
                                    }}>
                                        {prettyMilliseconds(timeLeft)}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    background: '#DCFCE7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16A34A' }} />
                                </div>

                                <div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: '#6B7280',
                                        textTransform: 'uppercase'
                                    }}>
                                        Ready
                                    </div>
                                    <div style={{
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        color: '#16A34A'
                                    }}>
                                        You can start your exam
                                    </div>
                                </div>
                            </>
                        )}

                    </div>

                    {/* Start Button */}
                    <Button
                        style={{
                            background: disable ? '#A5B4FC' : '#4F46E5',
                            border: 'none',
                            borderRadius: 8,
                            padding: '0.6rem 2rem',
                            fontWeight: 600,
                            fontSize: '0.95rem'
                        }}
                        onClick={() =>
                            disable
                                ? toast.error("Exam not started yet!")
                                : setModalShow(true)
                        }
                    >
                        Start Exam
                    </Button>

                </div>
            </div>

        </div>

        {/* Modal remains same */}
         <CustomModal
                displayModal={modalShow}
                onClose={() => setModalShow(false)}
                modalBodyClass="px-2"
                removeClose={true}
                size="lg"
                modalBody={
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 style={{ fontWeight: 700, margin: 0 }}>
                                Terms & Conditions
                            </h6>
                            <FontAwesomeIcon
                                icon={faTimesCircle}
                                onClick={() => setModalShow(false)}
                                style={{ cursor: "pointer", color: '#9CA3AF', fontSize: 18 }}
                            />
                        </div>

                        <div style={{
                            background: '#F8F9FA',
                            borderRadius: 8,
                            padding: '1rem',
                            marginBottom: '1rem',
                            fontSize: '0.85rem',
                            lineHeight: 1.7
                        }}>
                            <p>
                                I hereby confirm that I have carefully read and understood all the instructions provided for this examination. By proceeding, I agree to abide by the terms and conditions set forth for this exam.
                            </p>
                            <p style={{ margin: 0 }}>
                               I declare that I will maintain discipline and refrain from engaging in any disruptive or inappropriate behavior. Any violation may result in disciplinary action, including potential disqualification.
                            </p>
                        </div>

                        <Form.Check
                            type="checkbox"
                            id="terms-check"
                            label="I have read and understood the instructions and I am ready to start the exam."
                            className="mb-3"
                            checked={instructionsCheck}
                            onChange={() => setInstructionsCheck(!instructionsCheck)}
                        />

                        <Button
                            className="w-100"
                            onClick={generateTokenForLiveExam}
                            disabled={!instructionsCheck}
                            style={{
                                background: '#3B00A8',
                                border: 'none',
                                fontWeight: 600,
                                borderRadius: 8
                            }}
                        >
                            Start Exam
                        </Button>
                    </div>
                }
            />
    </>
);
}



